import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../../services/api";
import { bold, num, tableRows } from "../../utils/xlsx";
import {
  cellRight,
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  REPORTS,
  sheetHeading,
  signTone,
  StatCard,
  validPeriod,
} from "./common";

const ACTIVITIES = [
  { key: "", label: "All activities" },
  { key: "OPERATING", label: "Operating" },
  { key: "INVESTING", label: "Investing" },
  { key: "FINANCING", label: "Financing" },
];
const activityLabel = (key) => ACTIVITIES.find((a) => a.key === key)?.label || key;

const CategoryTable = ({ title, rows }) => (
  <div className="table-wrapper" style={{ flex: "1 1 360px", minWidth: 0, marginTop: 0 }}>
    <table>
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>{title}</th>
          <th>Activity</th>
          <th style={cellRight}>Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ color: "var(--color-text-faint)" }}>
              None in this period
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.group}>
              <td style={{ textAlign: "left" }}>{row.groupLabel}</td>
              <td>{activityLabel(row.activity)}</td>
              <td style={cellRight}>{money(row.amount)}</td>
            </tr>
          ))
        )}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
            Total
          </td>
          <td style={{ ...cellRight, fontWeight: 700 }}>{money(rows.reduce((sum, row) => sum + Number(row.amount), 0))}</td>
        </tr>
      </tfoot>
    </table>
  </div>
);

/** Cash inflow and outflow through cash and bank ledgers: by source / use, by activity and month by month. */
const CashFlow = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [applied, setApplied] = useState(null);
  const [activity, setActivity] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/cash-flow`, { fromDate: period.fromDate, toDate: period.toDate }));
      setApplied(period);
    } catch (error) {
      toast.error(error.message || "Failed to load cash flow");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byActivity = (rows) => rows.filter((row) => !activity || row.activity === activity);

  const exportExcel = () => {
    const heading = sheetHeading("Cash Flow", periodLabel(applied));
    const categoryColumns = [
      { label: "Account Group", key: "groupLabel" },
      { label: "Activity", value: (r) => activityLabel(r.activity) },
      { label: "Amount", key: "amount", format: "money" },
    ];
    const flowColumns = (first) => [
      first,
      { label: "Inflow", key: "inflow", format: "money" },
      { label: "Outflow", key: "outflow", format: "money" },
      { label: "Net Flow", key: "net", format: "money" },
    ];
    download(`Cash Flow ${periodLabel(applied)}`, [
      {
        name: "Summary",
        rows: [
          ...heading,
          ["Opening Cash & Bank", num(report.openingCashAndBank)],
          ["Add: Cash Inflow", num(report.totalInflow)],
          ["Less: Cash Outflow", num(report.totalOutflow)],
          [bold("Net Cash Flow"), bold(num(report.netCashFlow))],
          [bold("Closing Cash & Bank"), bold(num(report.closingCashAndBank))],
          [],
          ...tableRows(flowColumns({ label: "Activity", value: (r) => activityLabel(r.activity) }), report.byActivity, { totals: true }),
        ],
        widths: [28, 16, 16, 16],
      },
      { name: "Inflows", rows: [...heading, ...tableRows(categoryColumns, report.inflows, { totals: true })], widths: [32, 14, 16] },
      { name: "Outflows", rows: [...heading, ...tableRows(categoryColumns, report.outflows, { totals: true })], widths: [32, 14, 16] },
      { name: "Monthly", rows: [...heading, ...tableRows(flowColumns({ label: "Month", key: "month" }), report.monthly, { totals: true })], widths: [14, 16, 16, 16] },
    ]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Money actually received into and paid out of cash and bank accounts. Transfers between cash and bank (deposits, withdrawals) are not counted.
        Closing = Opening + Inflow − Outflow.
      </div>
      <div className="filters-bar">
        <PeriodFilter period={period} onChange={setPeriod} />
        <select value={activity} onChange={(e) => setActivity(e.target.value)} aria-label="Activity">
          {ACTIVITIES.map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </select>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Opening Cash & Bank" value={`₹${money(report.openingCashAndBank)}`} />
            <StatCard label="Cash Inflow" value={`₹${money(report.totalInflow)}`} tone="good" />
            <StatCard label="Cash Outflow" value={`₹${money(report.totalOutflow)}`} tone="bad" />
            <StatCard label="Net Cash Flow" value={`₹${money(report.netCashFlow)}`} tone={signTone(report.netCashFlow)} />
            <StatCard label="Closing Cash & Bank" value={`₹${money(report.closingCashAndBank)}`} />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-start" }}>
            <CategoryTable title="Inflow from" rows={byActivity(report.inflows)} />
            <CategoryTable title="Outflow to" rows={byActivity(report.outflows)} />
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Activity</th>
                  <th style={cellRight}>Inflow</th>
                  <th style={cellRight}>Outflow</th>
                  <th style={cellRight}>Net Flow</th>
                </tr>
              </thead>
              <tbody>
                {report.byActivity.map((row) => (
                  <tr key={row.activity}>
                    <td style={{ textAlign: "left" }}>{activityLabel(row.activity)}</td>
                    <td style={cellRight}>{money(row.inflow)}</td>
                    <td style={cellRight}>{money(row.outflow)}</td>
                    <td style={{ ...cellRight, color: Number(row.net) < 0 ? "var(--color-danger)" : undefined }}>{money(row.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {report.monthly.length > 0 ? (
            <>
              <h3 style={{ fontSize: "15px", fontWeight: 700, margin: "22px 0 8px" }}>Month-wise</h3>
              <div className="content-card" style={{ height: "280px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.monthly.map((m) => ({ month: m.month, Inflow: Number(m.inflow), Outflow: Number(m.outflow) }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(v) => Number(v).toLocaleString("en-IN", { notation: "compact" })} />
                    <Tooltip formatter={(value) => `₹${money(value)}`} />
                    <Legend />
                    <Bar dataKey="Inflow" fill="#2a9d6f" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Outflow" fill="#d9534f" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Month</th>
                      <th style={cellRight}>Inflow</th>
                      <th style={cellRight}>Outflow</th>
                      <th style={cellRight}>Net Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthly.map((row) => (
                      <tr key={row.month}>
                        <td style={{ textAlign: "left" }}>{row.month}</td>
                        <td style={cellRight}>{money(row.inflow)}</td>
                        <td style={cellRight}>{money(row.outflow)}</td>
                        <td style={{ ...cellRight, color: Number(row.net) < 0 ? "var(--color-danger)" : undefined }}>{money(row.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CashFlow;
